import { Component } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  // Safely convert error to string
  getErrorMessage = () => {
    const { error } = this.state;
    if (!error) return "An unexpected error occurred.";
    
    // Handle string errors
    if (typeof error === 'string') return error;
    
    // Handle Error objects with message
    if (error.message) return error.message;
    
    // Handle Pydantic validation errors or other objects
    if (typeof error === 'object') {
      try {
        return JSON.stringify(error, null, 2);
      } catch {
        return "An unexpected error occurred.";
      }
    }
    
    return String(error);
  };

  render() {
    if (this.state.hasError) {
      const errorMessage = this.getErrorMessage();
      
      return (
        <div className="flex items-center justify-center min-h-[400px] p-6">
          <Card className="bg-[#18181b] border-white/10 rounded-sm max-w-md w-full">
            <CardContent className="p-8 text-center">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-[#D92323]/20 rounded-full">
                  <AlertTriangle className="w-10 h-10 text-[#D92323]" />
                </div>
              </div>
              
              <h2 
                className="text-xl font-bold text-white uppercase tracking-wide mb-3"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Something Went Wrong
              </h2>
              
              <p className="text-[#A1A1AA] text-sm mb-6">
                {this.props.fallbackMessage || "We encountered an unexpected error. Please try again."}
              </p>
              
              <Button
                onClick={this.handleRetry}
                className="bg-[#D4AF37] hover:bg-[#b8962e] text-black font-semibold uppercase tracking-wider"
                style={{ fontFamily: 'Oswald, sans-serif' }}
                data-testid="error-boundary-retry-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="mt-6 p-3 bg-red-500/10 rounded-sm text-left overflow-auto max-h-40">
                  <p className="text-red-400 text-xs font-mono break-all whitespace-pre-wrap">
                    {errorMessage}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
